// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { Call } from '@azure/communication-calling';

/**
 * Connects call automation to an existing group call
 * @param call - The Call object from the established call
 * @param interviewId - Optional interview ID for custom interview context
 * @returns Promise that resolves when call automation is connected
 */
export interface InterviewCallContext {
  functionalArea: string;
  roleDesignation: string;
  jobDescription: string;
  candidateName?: string;
}

export const connectCallAutomationToGroupCall = async (
  call: Call,
  interviewId?: string,
  interviewContext?: InterviewCallContext,
  proficiencyLevel?: number
): Promise<boolean> => {
  if (call.info !== undefined && call.state === 'Connected') {
    const serverCallID = await call.info.getServerCallId();
    const requestBody: {
      serverCallId: string;
      interviewId?: string;
      interviewContext?: InterviewCallContext;
      proficiencyLevel?: number;
    } = {
      serverCallId: serverCallID
    };

    // Include interviewId if provided
    if (interviewId) {
      requestBody.interviewId = interviewId;
      console.log('Connecting call automation with interviewId:', interviewId);
    }
    if (interviewContext) {
      requestBody.interviewContext = interviewContext;
    }
    if (proficiencyLevel) {
      requestBody.proficiencyLevel = proficiencyLevel;
    }

    const response = await fetch('/joinGroupCallWithAutomation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });
    if (!response.ok) {
      if (response.status === 401) {
        window.dispatchEvent(new CustomEvent('auth-unauthorized'));
        throw new Error('Unauthorized');
      }
      throw new Error('Failed to start call with call automation: ' + response.statusText);
    }
    return true;
  }
  return false;
};
